import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AuthLayout } from '../layouts/AuthLayout';
import { Button, Notice, Spinner } from '../components/ui';
import { config } from '../config';
import { getInvite, ROLE_WORD, savePendingInvite } from '../lib/invites';
import { useSession } from '../lib/session';

const onPhone = typeof navigator !== 'undefined' && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

/**
 * Where an invite email's button lands (/join/:token). On a phone it opens the
 * app with the invite (or points to the store first); leaders can also join on
 * the web. Works signed in or out; the invite is for a new account.
 */
export function JoinPage() {
  const { token = '' } = useParams();
  const { state, signOut } = useSession();
  const invite = useQuery({ queryKey: ['invite', token], queryFn: () => getInvite(token), retry: false });

  // Remember it for this tab, so Google or Apple sign-up joins this school.
  useEffect(() => {
    if (invite.data?.status === 'open') savePendingInvite(token);
  }, [invite.data?.status, token]);

  if (invite.isLoading) {
    return (
      <div className="welcome-loading">
        <Spinner />
      </div>
    );
  }

  const inv = invite.data;
  if (!inv) {
    return (
      <AuthLayout title="This invite link isn’t valid" subtitle="Check you opened the whole link from the email, or ask your organisation to send a new invite.">
        <p className="auth-foot">
          Already have an account? <Link to="/sign-in">Sign in</Link>
        </p>
      </AuthLayout>
    );
  }

  if (inv.status !== 'open') {
    const copy = {
      used: { title: 'This invite has been used', subtitle: `Sign in to EducAitor with ${inv.email}.` },
      expired: { title: 'This invite has expired', subtitle: `Ask ${inv.inviterName ?? 'your organisation'} to send a new one.` },
      cancelled: { title: 'This invite was cancelled', subtitle: `Ask ${inv.inviterName ?? 'your organisation'} if you should still join.` },
    }[inv.status];
    return (
      <AuthLayout title={copy.title} subtitle={copy.subtitle}>
        <p className="auth-foot">
          <Link to="/sign-in">Go to sign in</Link>
        </p>
      </AuthLayout>
    );
  }

  const appLink = `${config.appScheme}://join?invite=${encodeURIComponent(token)}`;
  const leader = inv.role === 'school_leader';
  const signedInAs = state.status === 'signedIn' ? state.me.email : null;

  // EducAitor set this organisation up for them: they own it and set it up here, on the web.
  if (inv.makeOwner) {
    return (
      <AuthLayout
        personal={inv.personal}
        title={inv.personal ? 'Your EducAitor account is ready' : `Set up ${inv.schoolName}`}
        subtitle={
          inv.personal
            ? `${inv.inviterName ? `${inv.inviterName} at EducAitor` : 'EducAitor'} has made an account for ${inv.email}.`
            : `${inv.inviterName ? `${inv.inviterName} at EducAitor` : 'EducAitor'} has set up ${inv.schoolName} for ${inv.email}. You'll be its account owner.`
        }
      >
        {signedInAs ? (
          <Notice>
            You&rsquo;re signed in as {signedInAs}. This invite is for a new account.{' '}
            <button className="link-button" onClick={() => signOut()}>
              Sign out
            </button>
          </Notice>
        ) : (
          <>
            <Link className="btn btn-primary btn-lg btn-block" to={`/sign-up?invite=${encodeURIComponent(token)}`}>
              Create your account
            </Link>
            <p className="muted">
              Use <strong>{inv.email}</strong>.{' '}
              {inv.personal
                ? 'Then a few questions about your teaching, so your feedback fits. About two minutes.'
                : <>Then a short setup: your organisation&rsquo;s details, its framework and your team. About three minutes{onPhone ? ', and easiest on a computer' : ''}.</>}
            </p>
            <p className="auth-foot">
              Already have an account? <Link to="/sign-in">Sign in</Link>
            </p>
          </>
        )}
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={`Join ${inv.schoolName}`}
      subtitle={`${inv.inviterName ?? 'Your organisation'} invited ${inv.email} to EducAitor as ${ROLE_WORD[inv.role]}.`}
    >
      {signedInAs ? (
        <Notice>
          You&rsquo;re signed in as {signedInAs}. This invite is for a new account.{' '}
          <button className="link-button" onClick={() => signOut()}>
            Sign out
          </button>
        </Notice>
      ) : null}

      {onPhone ? (
        <div className="auth-form">
          <a className="btn btn-primary btn-lg btn-block" href={appLink}>
            Open in the EducAitor app
          </a>
          <p className="muted">
            Sign up with <strong>{inv.email}</strong>. You won&rsquo;t need an organisation code.
          </p>
          <div className="panel form-section">
            <p>
              <strong>Don&rsquo;t have the app yet?</strong>
            </p>
            {config.appStoreUrl || config.playStoreUrl ? (
              <div className="form-actions">
                {config.appStoreUrl ? (
                  <a className="btn btn-secondary btn-md" href={config.appStoreUrl}>
                    App Store
                  </a>
                ) : null}
                {config.playStoreUrl ? (
                  <a className="btn btn-secondary btn-md" href={config.playStoreUrl}>
                    Google Play
                  </a>
                ) : null}
              </div>
            ) : (
              <p className="muted">Search for EducAitor in the App Store or Google Play.</p>
            )}
            {config.appStoreUrl && !config.playStoreUrl ? <p className="muted">On Android? EducAitor is coming to Google Play soon.</p> : null}
            <p className="muted">Once it&rsquo;s installed, come back to this page and tap the button above.</p>
          </div>
        </div>
      ) : (
        <Notice>
          Open the invite email on your phone and tap the button there to join in the EducAitor app.
          {leader ? ' As a leader you can also join here on the web.' : ''}
        </Notice>
      )}

      {leader && !signedInAs ? (
        <Link className={`btn ${onPhone ? 'btn-ghost' : 'btn-primary'} btn-lg btn-block`} to={`/sign-up?invite=${encodeURIComponent(token)}`}>
          Join on the web instead
        </Link>
      ) : null}

      {!signedInAs ? (
        <p className="auth-foot">
          Already have an account? <Link to="/sign-in">Sign in</Link>
        </p>
      ) : (
        <Button variant="ghost" onClick={() => signOut()}>
          Sign out
        </Button>
      )}
    </AuthLayout>
  );
}
